import { useEffect, useMemo } from 'react'
import { useRecurringExpenseStore } from '@/store/recurringExpenseStore'
import { useRepoVersion } from '@/repository/repoVersionStore'
import {
  averageMonthlyExpenses,
  actualMonthExpenses,
  expenseBreakdownByCategory,
  monthOfYearFromString,
} from '@/engine/recurringExpenses'

/**
 * Primary facade for the Recurring Expenses module — components should never
 * touch the store directly. Auto-initializes on first use.
 *
 * @param activeMonth optional "YYYY-MM" string. When provided, the hook also
 *   returns this month's actual essential burden (vs the steady-state average).
 */
export function useRecurringExpenses(activeMonth?: string) {
  const expenses = useRecurringExpenseStore((s) => s.expenses)
  const isInitialized = useRecurringExpenseStore((s) => s.isInitialized)
  const initialize = useRecurringExpenseStore((s) => s.initialize)
  const addExpense = useRecurringExpenseStore((s) => s.addExpense)
  const updateExpense = useRecurringExpenseStore((s) => s.updateExpense)
  const removeExpense = useRecurringExpenseStore((s) => s.removeExpense)
  // v0.8.35 — see useDebts.ts for the rationale. Same repo-rebind race that
  // surfaced the empty Dashboard / Debt Registry tabs after Chrome tab-restore
  // also left this hook on an empty expenses list, breaking the Survival
  // Floor essentials computation post-sign-in.
  const repoVersion = useRepoVersion()

  useEffect(() => {
    void initialize()
  }, [initialize, repoVersion])

  const activeExpenses = useMemo(() => expenses.filter((e) => e.isActive), [expenses])

  const essentialAvgMonthly = useMemo(
    () => averageMonthlyExpenses(activeExpenses, { essentialOnly: true }),
    [activeExpenses]
  )

  const totalAvgMonthly = useMemo(() => averageMonthlyExpenses(activeExpenses), [activeExpenses])

  const thisMonthEssentials = useMemo(() => {
    if (!activeMonth) return null
    const m = monthOfYearFromString(activeMonth)
    if (Number.isNaN(m)) return null
    return actualMonthExpenses(activeExpenses, m, { essentialOnly: true })
  }, [activeExpenses, activeMonth])

  const thisMonthTotal = useMemo(() => {
    if (!activeMonth) return null
    const m = monthOfYearFromString(activeMonth)
    if (Number.isNaN(m)) return null
    return actualMonthExpenses(activeExpenses, m)
  }, [activeExpenses, activeMonth])

  const breakdown = useMemo(() => expenseBreakdownByCategory(activeExpenses), [activeExpenses])

  return {
    expenses,
    activeExpenses,
    isInitialized,
    essentialAvgMonthly,
    totalAvgMonthly,
    thisMonthEssentials,
    thisMonthTotal,
    breakdown,
    addExpense,
    updateExpense,
    removeExpense,
  }
}
